import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Btn } from "@/components/erp/ui";
import { toast } from "sonner";
import { Users, Lock, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const RuleRow = ({ stage, rule, emps, load, dis, set }) => {
  const ids = rule.employee_ids || [];
  const key = stage.replace(/\W+/g, "-").toLowerCase();
  return (
    <div className="p-3 rounded-lg border border-slate-200" data-testid={`pic-rule-${key}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-sm w-32">{stage}</span>
        <div className="flex flex-wrap gap-1.5 flex-1">
          {ids.map((id) => { const e = emps.find((x) => x.id === id); return (
            <span key={id} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded-full px-2 py-0.5" data-testid={`pic-chip-${key}-${id}`}>
              {e?.name || id} <span className="text-slate-400">· antrian {load[id] || 0}</span>
              {!dis && <button type="button" onClick={() => set((r) => ({ employee_ids: r.employee_ids.filter((x) => x !== id) }))}><X className="w-3 h-3 text-slate-400 hover:text-rose-500" /></button>}
            </span>); })}
          {ids.length === 0 && <span className="text-xs text-slate-400">Belum ada PIC — diisi manual di WO</span>}
        </div>
        {!dis && <select value="" onChange={(e) => { const v = e.target.value; if (v) set((r) => ({ employee_ids: r.employee_ids.includes(v) ? r.employee_ids : [...r.employee_ids, v] })); }} className="border border-slate-300 rounded-lg px-2 py-1 text-xs" data-testid={`pic-add-${key}`}>
          <option value="">+ Tambah orang</option>
          {emps.filter((e) => !ids.includes(e.id)).map((e) => <option key={e.id} value={e.id}>{e.name}{e.position ? ` · ${e.position}` : ""}</option>)}
        </select>}
      </div>
      {ids.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-600" data-testid={`pic-mode-${key}`}>
          <span>Cara kerja 2+ orang:</span>
          {[["together", "Semua jadi PIC bersama (bonus dibagi rata)"], ["auto", "Bergantian — antrian paling kosong"], ["manual", "Dipilih manual di WO"]].map(([m, l]) => (
            <label key={m} className="inline-flex items-center gap-1"><input type="radio" disabled={dis} name={`mode-${key}`} data-testid={`pic-mode-${key}-${m}`} checked={(rule.mode || (rule.auto === false ? "manual" : "auto")) === m} onChange={() => set(() => ({ mode: m, auto: m !== "manual" }))} />{l}</label>))}
        </div>
      )}
    </div>
  );
};

export const PicRulesSettings = () => {
  const { user } = useAuth();
  const dis = user?.role !== "owner";
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings/pic-rules").then(({ data }) => setD(data)).catch(() => setD({ rules: [], stages: [], load: {}, employees: [] }));
  }, []);
  if (!d) return <Card className="p-5 text-sm text-slate-400">Memuat…</Card>;
  const emps = d.employees || [];
  const ruleOf = (st) => d.rules.find((r) => r.stage.toLowerCase() === st.toLowerCase()) || { stage: st, employee_ids: [], auto: true };
  const setRule = (st, patch) => setD((x) => {
    const cur = x.rules.find((r) => r.stage.toLowerCase() === st.toLowerCase()) || { stage: st, employee_ids: [], auto: true };
    return { ...x, rules: [...x.rules.filter((r) => r !== cur), { ...cur, ...patch(cur) }] };
  });
  const save = async () => {
    setBusy(true);
    try { await api.put("/settings/pic-rules", { rules: d.rules }); toast.success("Aturan PIC otomatis disimpan"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setBusy(false); }
  };
  return (
    <Card className="p-5" data-testid="pic-rules-settings">
      <div className="flex items-center gap-2 mb-1"><Users className="w-4 h-4" /><h3 className="font-semibold">PIC Otomatis per Tahap</h3>{dis && <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> hanya admin utama yang bisa mengubah</span>}</div>
      <p className="text-xs text-slate-500 mb-4">Tentukan siapa pegang tahap mana. Saat WO dibuat, PIC (plan) terisi otomatis. Jika 1 tahap dipegang 2+ orang, pilih per tahap: semua jadi PIC bersama, bergantian (antrian paling sedikit), atau manual. Di WO, PIC bisa diganti dan &quot;Dikerjakan oleh (aktual)&quot; bisa diisi bila yang mengerjakan berbeda — bonus payroll mengikuti aktual.</p>
      <div className="space-y-2">
        {d.stages.map((st) => <RuleRow key={st} stage={st} rule={ruleOf(st)} emps={emps} load={d.load || {}} dis={dis} set={(p) => setRule(st, p)} />)}
      </div>
      {!dis && <div className="mt-4"><Btn data-testid="pic-rules-save" disabled={busy} onClick={save}>{busy ? "Menyimpan…" : "Simpan Aturan PIC"}</Btn></div>}
    </Card>
  );
};
